import React, { useCallback, useEffect, useRef } from 'react';
import './Heatmap.css';

// --- Helper functions from optimized_client_functions.js ---
function createHeatmapMatrix(heatmapData) {
    const matrix = Array(7).fill(0).map(() => Array(24).fill(0.0));
    if (heatmapData && heatmapData.hm) {
        for (const index in heatmapData.hm) {
            const day = Math.floor(parseInt(index, 10) / 24);
            const hour = parseInt(index, 10) % 24;
            matrix[day][hour] = heatmapData.hm[index];
        }
    }
    return matrix;
}

function formatHourForDisplay(hour) {
    if (hour === 0) return '12am';
    if (hour < 12) return `${hour}am`;
    if (hour === 12) return '12pm';
    return `${hour - 12}pm`;
}

const Heatmap = ({ heatmapData, timezone }) => {
    const containerRef = useRef(null);

    const renderHeatmap = useCallback((container, data) => {
        container.innerHTML = '';
        const matrix = createHeatmapMatrix(data);
        const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

        // Get current day and hour
        const now = new Date();
        const jsDay = now.getDay(); // Sunday is 0, Monday is 1, etc.
        const currentDayIndex = (jsDay === 0) ? 6 : jsDay - 1; // Convert to matrix index (Mon=0, Sun=6)
        const currentHour = now.getHours();

        // Render hour labels (now in user's local time)
        for (let i = 0; i < 24; i++) {
            const label = document.createElement('div');
            label.classList.add('grid-label');
            label.style.gridRow = 1;
            label.style.gridColumn = i + 2;
            label.textContent = i % 3 === 0 ? formatHourForDisplay(i) : '';
            container.appendChild(label);
        }

        // Render day labels
        DAYS.forEach((day, index) => {
            const label = document.createElement('div');
            label.classList.add('grid-label');
            label.style.gridRow = index + 2;
            label.style.gridColumn = 1;
            label.textContent = day;
            container.appendChild(label);
        });

        // Render heatmap cells
        matrix.forEach((dayRow, dayIndex) => {
            dayRow.forEach((score, hourIndex) => {
                const cell = document.createElement('div');
                cell.classList.add('heatmap-cell');
                cell.style.gridRow = dayIndex + 2;
                cell.style.gridColumn = hourIndex + 2;

                // Highlight the current cell
                if (dayIndex === currentDayIndex && hourIndex === currentHour) {
                    cell.classList.add('current-time-cell');
                }

                const scoreText = document.createElement('div');
                scoreText.classList.add('cell-score-text');

                if (score > 0) {
                    const roundedScore = Math.round(score / 10) * 10;
                    const greenShadeIndex = Math.min(9, Math.floor(roundedScore / 10));
                    const blueTealColors = [
                        '#e8f5f7', '#d1e7eb', '#b9dbe0', '#a2ced4', '#8bc2c9',
                        '#74b5be', '#5da8b3', '#469ba8', '#2f8e9d', '#1F7A8C'
                    ];
                    cell.style.backgroundColor = blueTealColors[greenShadeIndex];
                    scoreText.textContent = `${roundedScore}%`;
                } else {
                    scoreText.textContent = '0%';
                }
                cell.appendChild(scoreText);
                container.appendChild(cell);
            });
        });

        // Render timezone info
        const timezoneInfoDiv = document.createElement('div');
        timezoneInfoDiv.classList.add('timezone-info');
        timezoneInfoDiv.textContent = `Times shown in ${timezone} timezone`;
        container.appendChild(timezoneInfoDiv);
    }, [timezone]);

    useEffect(() => {
        if (containerRef.current && heatmapData) {
            renderHeatmap(containerRef.current, heatmapData);
        }
    }, [heatmapData, timezone, renderHeatmap]);

    return <div ref={containerRef} id="heatmap-container"></div>;
};

export default Heatmap;
